import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { UserPlus, Shield, Briefcase, User, Check, X } from 'lucide-react';

const UserFormModal = ({
  open,
  onClose,
  mode, // 'create' or 'edit'
  formData,
  onFormChange,
  onSubmit,
  clients
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  const toggleClient = (clientId) => {
    const current = formData.assigned_client_ids || [];
    if (current.includes(clientId)) {
      onFormChange({
        ...formData,
        assigned_client_ids: current.filter(id => id !== clientId)
      });
    } else {
      onFormChange({
        ...formData,
        assigned_client_ids: [...current, clientId]
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            {mode === 'create' ? 'Create New User' : 'Edit User'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="full_name">Full Name</Label>
            <Input
              id="full_name"
              value={formData.full_name || ''}
              onChange={(e) => onFormChange({ ...formData, full_name: e.target.value })}
              placeholder="John Doe"
              data-testid="user-name-input"
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => onFormChange({ ...formData, email: e.target.value })}
              placeholder="user@example.com"
              disabled={mode === 'edit'}
              data-testid="user-email-input"
            />
          </div>

          <div>
            <Label htmlFor="role">Role</Label>
            <Select
              value={formData.role || 'employee'}
              onValueChange={(value) => onFormChange({ ...formData, role: value })}
            >
              <SelectTrigger data-testid="user-role-select">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-red-500" />
                    Admin (Full Access)
                  </div>
                </SelectItem>
                <SelectItem value="employee">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-blue-500" />
                    Employee (Limited Access)
                  </div>
                </SelectItem>
                <SelectItem value="customer">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500" />
                    Customer (View Only)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.role !== 'admin' && (
            <div>
              <Label>Assigned Clients</Label>
              <div className="mt-2 border rounded-lg p-3 max-h-48 overflow-y-auto">
                {clients.length === 0 ? (
                  <p className="text-sm text-slate-500">No clients available</p>
                ) : (
                  <div className="space-y-2">
                    {clients.map(client => {
                      const isAssigned = (formData.assigned_client_ids || []).includes(client.id);
                      return (
                        <div
                          key={client.id}
                          onClick={() => toggleClient(client.id)}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                            isAssigned ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 hover:bg-slate-100'
                          }`}
                          data-testid={`client-option-${client.id}`}
                        >
                          <span className="text-sm">{client.name}</span>
                          {isAssigned ? (
                            <Check className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <X className="w-4 h-4 text-slate-300" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {formData.role !== 'admin' && (
                <p className="text-xs text-slate-500 mt-1">
                  {(formData.assigned_client_ids || []).length} client(s) selected
                </p>
              )}
            </div>
          )}

          {mode === 'edit' && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active !== false}
                onChange={(e) => onFormChange({ ...formData, is_active: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_active">Account Active</Label>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" data-testid="submit-user-btn">
              {mode === 'create' ? 'Create User' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserFormModal;
